import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/**
 * M12 §29 #2 — Approval Queue (dedicated screen).
 *
 * A focused, filterable work-queue for management reviewers, sourced from the
 * same `/v1/sh/dashboard/queue` endpoint the dashboard summarises. Unlike the
 * dashboard (which folds the queue among tiles/budget/summaries), this screen
 * lets a reviewer filter by status and priority and drill into a review.
 */
interface QueueItem {
  readonly reviewId: string;
  readonly jobNumber: string | null;
  readonly status: string;
  readonly reviewerRoleCode: string | null;
  readonly priorityCode: string | null;
  readonly slaDeadlineAt: string;
  readonly estimatedCostInr: number | null;
  readonly isHighValue: boolean;
  readonly cycleNo: number;
}

const STATUS_OPTIONS = [
  'PENDING_REVIEW',
  'IN_REVIEW',
  'CLARIFICATION_REQUIRED',
  'ESCALATED',
  'ON_HOLD',
] as const;


function slaColor(iso: string): string {
  const d = new Date(iso);
  if (d < new Date()) return 'var(--color-danger)';
  const hours = (d.getTime() - Date.now()) / 3600000;
  if (hours < 6) return 'var(--color-warning)';
  return 'inherit';
}

export function ShApprovalQueuePage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<QueueItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string>('');
  const [priority, setPriority] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (status) qs.set('status', status);
      if (priority) qs.set('priorityCode', priority);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const q = await apiRequest<QueueItem[]>(`/v1/sh/dashboard/queue${suffix}`, { token });
      setRows(Array.isArray(q) ? q : []);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token, status, priority]);

  useEffect(() => { void load(); }, [load]);

  return (
    <main aria-label="sh-approval-queue">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to="/sh/dashboard" data-testid="back-dashboard">{t('sh.common.dashboard')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('sh.queue.title')}</h1>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}

      <section aria-label="queue-filters" style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label htmlFor="q-status" style={{ fontSize: 11, display: 'block' }}>{t('sh.common.status')}</label>
          <select id="q-status" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status"
                  style={{ padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
            <option value="">{t('sh.queue.allStatuses')}</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="q-priority" style={{ fontSize: 11, display: 'block' }}>{t('sh.common.priority')}</label>
          <select id="q-priority" value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="filter-priority"
                  style={{ padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
            <option value="">{t('sh.queue.allPriorities')}</option>
            <option value="HIGH">{t('sh.queue.priorityHigh')}</option>
            <option value="MEDIUM">{t('sh.queue.priorityMedium')}</option>
            <option value="LOW">{t('sh.queue.priorityLow')}</option>
          </select>
        </div>
      </section>

      {loading ? (
        <p>{t('sh.common.loading')}</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }} data-testid="queue-empty">{t('sh.queue.empty')}</p>
      ) : (
        <DataTable<QueueItem>
          ariaLabel="sh-queue-table"
          rows={rows}
          rowKey={(r) => r.reviewId}
          rowAriaLabel={(r) => `row-${r.reviewId}`}
          columns={[
            { key: 'job', header: t('sh.common.job'), render: (r) => r.jobNumber ?? '—' },
            { key: 'status', header: t('sh.common.status'), render: (r) => <span aria-label={`status-${r.reviewId}`}>{r.status}</span> },
            { key: 'priority', header: t('sh.common.priority'), render: (r) => r.priorityCode ?? '—' },
            { key: 'slaDue', header: t('sh.common.slaDue'), render: (r) => <span style={{ color: slaColor(r.slaDeadlineAt) }}>{at(r.slaDeadlineAt)}</span> },
            { key: 'cost', header: t('sh.common.cost'), render: (r) => money(r.estimatedCostInr) },
            { key: 'hv', header: t('sh.common.hv'), render: (r) => (r.isHighValue ? '🔺' : '—') },
            { key: 'cycle', header: t('sh.common.cycle'), render: (r) => String(r.cycleNo) },
            {
              key: 'actions', header: '',
              render: (r) => (
                <Link to={`/sh/review/${r.reviewId}`} aria-label={`open-${r.reviewId}`} data-testid={`open-review-${r.reviewId}`}>{t('sh.common.open')}</Link>
              ),
            },
          ]}
        />
      )}
    </main>
  );
}
